import type { ReactNode } from "react";

export type FilterAccordionProps = {
  id: string;
  title: string;
  summary?: ReactNode;
  active?: boolean;
  open: boolean;
  onToggle: (id: string) => void;
  toggleAria: string;
  activeLabel: string;
  children: ReactNode;
};

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

/**
 * Collapsible filter section. Collapsed by default; when collapsed and active
 * it shows a summary chip of the current value so the user can read their
 * selection without expanding. Height animates via the CSS grid-rows trick
 * (DESIGN_CONTEXT §17).
 */
export function FilterAccordion({
  id,
  title,
  summary,
  active = false,
  open,
  onToggle,
  toggleAria,
  activeLabel,
  children,
}: FilterAccordionProps) {
  const bodyId = `filter-section-${id}`;
  return (
    <div
      className={[
        // shrink-0: keep natural height inside the scrollable flex body — without
        // it, multiple open sections get compressed and their content clipped.
        "shrink-0 overflow-hidden rounded-xl border bg-[color:var(--surface)] transition-colors",
        active
          ? "border-[color-mix(in_oklab,var(--accent-500)_30%,var(--card))]"
          : "border-[color:var(--border-light)]",
      ].join(" ")}
    >
      <button
        type="button"
        onClick={() => onToggle(id)}
        aria-expanded={open}
        aria-controls={bodyId}
        aria-label={toggleAria}
        className="flex min-h-11 w-full items-center gap-2.5 px-3.5 py-2.5 text-start transition-colors hover:bg-[color:var(--background-muted)]"
      >
        {active ? (
          <span
            className="h-1.5 w-1.5 shrink-0 rounded-full bg-[color:var(--accent-500)]"
            aria-hidden="true"
          />
        ) : null}
        <span className="text-[13px] font-semibold text-[color:var(--text-primary)]">
          {title}
        </span>

        {/* Collapsed + active → summary chip. Hidden while expanded (the value
            is visible inside). */}
        {!open && summary ? (
          <span
            className={[
              "ms-auto max-w-[55%] truncate rounded-full px-2 py-0.5 text-[12px] font-semibold",
              active
                ? "bg-[color-mix(in_oklab,var(--accent-500)_10%,var(--card))] text-[color:var(--accent-600)]"
                : "bg-[color:var(--background-muted)] text-[color:var(--text-secondary)]",
            ].join(" ")}
            title={typeof summary === "string" ? summary : undefined}
          >
            {active ? <span className="sr-only">{activeLabel}: </span> : null}
            {summary}
          </span>
        ) : null}

        <ChevronIcon
          className={[
            (!open && summary) ? "ms-2.5" : "ms-auto",
            "shrink-0 text-[color:var(--text-muted)] transition-transform duration-300",
            open ? "rotate-180" : "",
          ].join(" ")}
        />
      </button>

      <div
        id={bodyId}
        className={[
          "grid transition-all duration-300 ease-in-out",
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        ].join(" ")}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="px-3.5 pb-3.5 pt-0.5">{children}</div>
        </div>
      </div>
    </div>
  );
}
